import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { RouteReuseStrategy, RouterModule, Routes } from '@angular/router';
import { RouteReloadStrategy } from '@services/route-reload-strategy/route-reload-strategy';
import { HomeComponent } from '@app/home';
import { LanguageComponent } from '@compounds/language/1.0';
import { AuthGuard } from '@guards/auth-guard/auth-guard';
import { permission } from '@services/permissions/1.0/permissions.service';
import { NgxPermissionsGuard } from 'ngx-permissions';
import { CanActivateWithBrandInformation } from './guards';
import { AuthErrorComponent } from './auth-error/auth-error.component';
import { RoleRoutingComponent } from './role-routing/role-routing.component';
import { SharedModule } from './shared.module';

const ROUTES: Routes = [
  {
    path: '',
    component: HomeComponent,
  },
  {
    path: 'auth-error',
    component: AuthErrorComponent,
  },
  {
    path: 'role-routing',
    component: RoleRoutingComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'brand',
    loadChildren: () => import('./brand/brand.module').then((mod) => mod.BrandModule),
    canLoad: [AuthGuard],
    canActivate: [NgxPermissionsGuard],
    data: {
      permissions: { only: () => getTenantPermissions('tenant:read') },
    },
  },
  {
    path: 'orders',
    loadChildren: () => import('./orders/orders.module').then((mod) => mod.OrdersModule),
    canLoad: [AuthGuard],
    canActivate: [NgxPermissionsGuard, CanActivateWithBrandInformation],
    data: {
      permissions: { only: () => getTenantPermissions('order:read') },
    },
  },
  {
    path: 'boss',
    loadChildren: () => import('./boss/boss.module').then((mod) => mod.BossModule),
    canLoad: [AuthGuard],
    canActivate: [NgxPermissionsGuard, CanActivateWithBrandInformation],
    data: {
      permissions: { only: () => getTenantPermissions('boss.entity:read') },
    },
  },
  {
    path: 'store',
    loadChildren: () => import('./store/store.module').then((mod) => mod.StoreModule),
    canLoad: [AuthGuard],
    canActivate: [NgxPermissionsGuard, CanActivateWithBrandInformation],
    data: {
      permissions: { only: () => getTenantPermissions('store:read') },
    },
  },
  {
    path: 'catalog',
    loadChildren: () => import('./catalog/catalog.module').then((mod) => mod.CatalogModule),
    canLoad: [AuthGuard],
    canActivate: [NgxPermissionsGuard, CanActivateWithBrandInformation],
    data: {
      permissions: { only: () => getTenantPermissions('catalog:write') },
    },
  },
  {
    path: 'customers',
    loadChildren: () => import('./customers/customers.module').then((mod) => mod.CustomersModule),
    canLoad: [AuthGuard],
    canActivate: [NgxPermissionsGuard, CanActivateWithBrandInformation],
    data: {
      permissions: { only: () => getTenantPermissions('user:read') },
    },
  },
  {
    path: 'coupons',
    loadChildren: () => import('./coupons/coupons.module').then((mod) => mod.CouponsModule),
    canLoad: [AuthGuard],
    canActivate: [NgxPermissionsGuard, CanActivateWithBrandInformation],
    data: {
      permissions: { only: () => getTenantPermissions('coupon-management:read') },
    },
  },
  {
    path: 'menu',
    loadChildren: () => import('./menu/menu.module').then((mod) => mod.MenuModule),
    canLoad: [AuthGuard],
    canActivate: [NgxPermissionsGuard, CanActivateWithBrandInformation],
    data: {
      permissions: { only: () => getTenantPermissions('menu:read') },
    },
  },
  {
    path: 'report',
    loadChildren: () => import('./report/report.module').then((mod) => mod.ReportModule),
    canLoad: [AuthGuard],
    canActivate: [NgxPermissionsGuard, CanActivateWithBrandInformation],
    data: {
      permissions: { only: () => getTenantPermissions('report:read') },
    },
  },
  {
    path: 'users',
    loadChildren: () => import('./users/users.module').then((mod) => mod.UsersModule),
    canLoad: [AuthGuard],
    canActivate: [NgxPermissionsGuard, CanActivateWithBrandInformation],
    data: {
      permissions: { only: () => getTenantPermissions('user:read') },
    },
  },
  {
    path: 'loyalty',
    loadChildren: () => import('./loyalty/loyalty.module').then((mod) => mod.LoyaltyModule),
    canLoad: [AuthGuard],
    canActivate: [NgxPermissionsGuard, CanActivateWithBrandInformation],
    data: {
      permissions: { only: () => getTenantPermissions('coupon-management:read') },
    },
  },
  {
    path: 'call-center/manage',
    loadChildren: () =>
      import('./call-center/manage/call-center-manage.module').then(
        (mod) => mod.CallCenterManageModule,
      ),
    canLoad: [AuthGuard],
    canActivate: [NgxPermissionsGuard, CanActivateWithBrandInformation],
    data: {
      permissions: { only: () => getTenantPermissions('call-centers:write') },
    },
  },
  {
    path: 'call-center/app',
    loadChildren: () =>
      import('./call-center/app/call-center-app.module').then((mod) => mod.CallCenterAppModule),
    canLoad: [AuthGuard],
    canActivate: [NgxPermissionsGuard, CanActivateWithBrandInformation],
    data: {
      permissions: { only: () => getTenantPermissions('call-centers:read') },
    },
  },
  {
    path: 'reseller',
    loadChildren: () => import('./reseller/reseller.module').then((mod) => mod.ResellerModule),
    canLoad: [AuthGuard],
    canActivate: [NgxPermissionsGuard],
    data: {
      permissions: { only: () => getTenantPermissions('reseller:read') },
    },
  },
];

function getTenantPermissions(permissionName: keyof typeof permission) {
  const brandId = sessionStorage.getItem('brand_id');
  // If session storage is cleared (e.g. new tab or browser reopened), fallback to local storage
  const resellerId =
    sessionStorage.getItem('reseller_id') || localStorage.getItem('active_tenant_identifier');
  const permissions = [
    'SuperAdmin',
    `${brandId}:*`,
    permissionName,
    `${brandId}:${permissionName}`,
  ];

  // For ResellerProductOperations users, also include permissions scoped to resellerId
  // when brand_id is not set (e.g., when viewing brand list)
  if (!brandId && resellerId) {
    permissions.push(`${resellerId}:*`, `${resellerId}:${permissionName}`);
  }

  return permissions;
}

const TRANSLATED_ROUTES: Routes = [
  {
    path: '',
    component: LanguageComponent,
    children: ROUTES,
  },
  {
    path: ':lang',
    component: LanguageComponent,
    children: ROUTES,
  },
];

@NgModule({
  imports: [
    CommonModule,
    RouterModule.forRoot(TRANSLATED_ROUTES, {
      // Enable same URL navigation with reload to support route refresh functionality
      onSameUrlNavigation: 'reload',
      // Use enableTracing to get verbose route logs in console
      // { enableTracing: true },
    }),
    RouterModule.forChild(ROUTES),
    SharedModule,
    AuthErrorComponent,
    RoleRoutingComponent,
  ],
  exports: [RouterModule],
  providers: [
    CanActivateWithBrandInformation,
    { provide: RouteReuseStrategy, useClass: RouteReloadStrategy },
  ],
})
export class AppRoutingModule {}
